import React, { useEffect, useMemo, useRef } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L, { DivIcon, LatLngExpression } from 'leaflet';
import {
	ClusterIconPosition,
	ClusterIconPreset,
	ClusterIconsPresets,
	FleetDto,
	PinVariant,
} from '../Map.interface';
import ReactDOMServer from 'react-dom/server';
import NewTruckIcon from '../../../assets/NewTruckIcon';
import { T } from '@tolgee/react';

const leafletIconFabric = ({
	width,
	height,
	iconAnchor,
	position,
	verticalOffset = 0,
	html,
	className,
}: ClusterIconPreset): DivIcon => {
	let positionAnchor: [number, number] = [width / 2, height / 2];

	if (position === ClusterIconPosition.top) {
		positionAnchor = [width / 2, 0];
	}
	if (position === ClusterIconPosition.bottom) {
		positionAnchor = [width / 2, height];
	}

	positionAnchor[1] += verticalOffset;

	return L.divIcon({
		className,
		html: ReactDOMServer.renderToString(html),
		iconSize: L.point(width, height, true),
		iconAnchor: iconAnchor || L.point(...positionAnchor, true),
	});
};

const icons: ClusterIconsPresets = {
	[PinVariant.truck]: {
		html: <NewTruckIcon />,
		className: '',
		width: 36,
		height: 36,
	},
};

const ClusterMarker = ({
	position,
	variant,
	truck,
}: {
	position: LatLngExpression;
	variant: PinVariant.truck;
	truck: FleetDto;
}) => {
	const truckDataRef = useRef(truck);
	const markerRef = useRef<any>(null);

	useEffect(() => {
		truckDataRef.current = truck;
	}, [truck]);

	const eventHandlers = useMemo(
		() => ({
			mouseover: () => {
				if (markerRef.current) {
					markerRef.current.openPopup();
				}
			},
			mouseout: () => {
				if (markerRef.current) {
					markerRef.current.closePopup();
				}
			},
		}),
		[],
	);

	return (
		<Marker
			ref={markerRef}
			position={position}
			icon={leafletIconFabric(icons[variant])}
			eventHandlers={eventHandlers}
		>
			<Popup
				className="cluster-popup"
				closeButton={false}
				closeOnClick
				offset={[-2, -21]}
			>
				<div className="flex flex-col gap-[6px]">
					<div className="flex items-center gap-3">
						<p className="whitespace-nowrap text-[16px] leading-[22px] text-gray-80">
							{truckDataRef.current?.name}
						</p>

						<div className="w-fit h-fit flex justify-center items-center bg-mountain-meadow rounded-full px-[6px]">
							<p className="text-[12px] leading-[16px] text-white">
								<T keyName={`statuses.${truckDataRef.current.status}`}>
									{truckDataRef.current.status}
								</T>
							</p>
						</div>
					</div>

					<div className="flex items-center justify-center gap-3">
						<p className="text-[12px] leading-[16px] text-gray-40">
							ID: {truckDataRef.current.id}
						</p>
						<div className="w-[2px] h-[2px] bg-gray-40 rounded-full" />
						<p className="text-[12px] leading-[16px] text-gray-40">
							{truckDataRef.current.type}
						</p>
					</div>
				</div>
			</Popup>
		</Marker>
	);
};

export default ClusterMarker;
